import { createSelector } from 'reselect';
import {
  getDividendStatements,
  getSummary,
  getAnalysis,
} from '@company/redux/selectors';
import type { SelectedStatement } from '@company/redux/interface';
import { toSignificant, toPercentage } from '@simplywallst/formatting';
import { differenceInDays } from 'date-fns';
import { get } from 'lodash';
import { getAveragesSelector } from '@ducks/averages/selectors';

interface PayoutEntity {
  label: 'paid' | 'retained';
  value: number;
}

export interface DividendStatements {
  isDividendSignificant: SelectedStatement;
  isDividendYieldTopTier: SelectedStatement;
  isDividendStable: SelectedStatement;
  isDividendGrowing: SelectedStatement;
  isDividendCovered: SelectedStatement;
  isDividendCoveredIn3Years: SelectedStatement;
  isDividendCoveredByFreeCashFlow: SelectedStatement;
}

export interface Dividend {
  score: number;
  marketName: string;
  exchangeCountryISO: string;
  isFund: boolean;
  isBank: boolean;
  companyId: string;
  companyName: string;
  uniqueSymbol: string;
  dividendNextDate: number;
  showUpcomingDividendPayment: boolean;
  companyNamePlural: string;
  statements: DividendStatements;
  dividendNextPayDate: number;
  industryName: string;
  dividendYield: number | null;
  dividendYieldFuture: number | null;
  keyMetric: string;
  keyMetricTitle: string;
  dividendYieldFormatted: string;
  marketDividendYieldP25Formatted: string;
  marketDividendYieldP75Formatted: string;
  industryDividendYieldFormatted: string;
  dividendYieldFutureFormatted: string;
  marketDividendYieldP25: number;
  marketDividendYieldP75: number;
  industryDividendYield: number;
  currentDividendPayout: PayoutEntity[];
  currentDividendPayoutRatio: number;
  currentDividendPayoutRatioFormatted: string;
  futureDividendPayout: PayoutEntity[];
  futureDividendPayoutRatio: number;
  futureDividendPayoutRatioFormatted: string;
  cashDividendPayout: PayoutEntity[];
  cashDividendPayoutRatio: number;
  cashDividendPayoutRatioFormatted: string;
}

interface KeyMetric {
  value: string;
  title: string;
}

const getDividendKeyMetic = (
  dividendYieldFuture: number,
  dividendYield: number
): KeyMetric => {
  let value = '';
  let title = '';
  if (dividendYield) {
    value = toPercentage(dividendYield, 2);
    title = 'Current Dividend Yield';
  } else if (dividendYieldFuture) {
    value = toPercentage(dividendYieldFuture, 2);
    title = 'Forecast Dividend Yield';
  }
  return {
    value,
    title,
  };
};

const toSignificantNumber = (value: number, significance = 1) =>
  Number(toSignificant(value * 100, significance));

const formatter = (value: number | null, significance = 1) =>
  value !== null ? toPercentage(value, significance) : 'n/a';

export const getDividendSectionSelector = createSelector(
  [getAnalysis, getSummary, getDividendStatements, getAveragesSelector],
  (analysis, summary, dividendStatements, averages): Dividend => {
    const { getIndustryAverage, getMarketAverage } = averages;
    const dividendYieldFuture = analysis.dividendYieldFuture;
    const dividendYield = analysis.dividendYield;
    const dividendKeyMetic = getDividendKeyMetic(
      dividendYieldFuture,
      dividendYield
    );
    const isBank = dividendStatements.isBank.value === 'Pass';
    const dividendNextDate = get(analysis, ['upcomingDividend', 'date'], 0);
    const showUpcomingDividendPayment =
      differenceInDays(new Date(dividendNextDate), Date.now()) > 0;

    const marketDividendYieldP25 = getMarketAverage('dividendYieldP25').value;
    const marketDividendYieldP75 = getMarketAverage('dividendYieldP75').value;
    const industryDividendYield = getIndustryAverage('dividendYield').value;

    const dividendYieldFormatted = formatter(dividendYield);
    const marketDividendYieldP25Formatted = formatter(marketDividendYieldP25);
    const marketDividendYieldP75Formatted = formatter(marketDividendYieldP75);
    const industryDividendYieldFormatted = formatter(industryDividendYield);
    const dividendYieldFutureFormatted = formatter(dividendYieldFuture);

    const payoutRatio = analysis.payoutRatio;
    const currentDividendPayoutRatioFormatted = formatter(payoutRatio, 0);
    const currentDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(payoutRatio < 0 ? 1 : payoutRatio, 0),
      },
    ];
    if (payoutRatio < 1 && payoutRatio >= 0) {
      currentDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - payoutRatio, 0),
      });
    }

    const futurePayoutRatio = analysis.payoutRatio3y;
    const futureDividendPayoutRatioFormatted = formatter(futurePayoutRatio, 0);
    const futureDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(
          futurePayoutRatio < 0 ? 1 : futurePayoutRatio,
          0
        ),
      },
    ];
    if (futurePayoutRatio < 1 && futurePayoutRatio >= 0) {
      futureDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - futurePayoutRatio, 0),
      });
    }

    const cashPayoutRatio = analysis.cashPayoutRatio;
    const cashDividendPayoutRatioFormatted = formatter(cashPayoutRatio, 0);
    const cashDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(
          cashPayoutRatio < 0 ? 1 : cashPayoutRatio,
          0
        ),
      },
    ];
    if (cashPayoutRatio < 1 && cashPayoutRatio >= 0) {
      cashDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - cashPayoutRatio, 0),
      });
    }

    return {
      keyMetric: dividendKeyMetic.value,
      keyMetricTitle: dividendKeyMetic.title,
      score: summary.score.income,
      industryName: summary.industryData.secondaryName,
      isFund: summary.isFund,
      isBank,
      marketName: getMarketAverage('netIncomeGrowthAnnual').source,
      exchangeCountryISO: summary.exchangeCountryISO,
      companyId: summary.companyId,
      companyName: summary.shortName,
      uniqueSymbol: summary.uniqueSymbol,
      dividendNextDate,
      showUpcomingDividendPayment,
      statements: dividendStatements,
      dividendNextPayDate: get(analysis, ['upcomingDividend', 'payDate'], 0),
      companyNamePlural: summary.shortNamePlural,
      dividendYield,
      marketDividendYieldP25,
      marketDividendYieldP75,
      industryDividendYield,
      dividendYieldFuture,
      dividendYieldFormatted,
      marketDividendYieldP25Formatted,
      marketDividendYieldP75Formatted,
      industryDividendYieldFormatted,
      dividendYieldFutureFormatted,
      currentDividendPayout,
      currentDividendPayoutRatio: payoutRatio,
      currentDividendPayoutRatioFormatted,
      futureDividendPayout,
      futureDividendPayoutRatio: futurePayoutRatio,
      futureDividendPayoutRatioFormatted,
      cashDividendPayout,
      cashDividendPayoutRatio: cashPayoutRatio,
      cashDividendPayoutRatioFormatted,
    };
  }
);

export const currentPayoutChartOptions = createSelector(
  [getAnalysis],
  (analysis) => {
    const payoutRatio = analysis.payoutRatio;
    const currentDividendPayoutRatioFormatted = formatter(payoutRatio, 0);
    const currentDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(payoutRatio < 0 ? 1 : payoutRatio, 0),
      },
    ];
    if (payoutRatio < 1 && payoutRatio >= 0) {
      currentDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - payoutRatio, 0),
      });
    }
    return {
      data: currentDividendPayout,
      payoutRatio: payoutRatio,
      payoutRatioFormatted: currentDividendPayoutRatioFormatted,
    };
  }
);

export const futurePayoutChartOptions = createSelector(
  [getAnalysis],
  (analysis) => {
    const futurePayoutRatio = analysis.payoutRatio3y;
    const futureDividendPayoutRatioFormatted = formatter(futurePayoutRatio, 0);
    const futureDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(
          futurePayoutRatio < 0 ? 1 : futurePayoutRatio,
          0
        ),
      },
    ];
    if (futurePayoutRatio < 1 && futurePayoutRatio >= 0) {
      futureDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - futurePayoutRatio, 0),
      });
    }
    return {
      payoutRatio: futureDividendPayout,
      dividendPayoutRatio: futurePayoutRatio,
      dividendPayoutRatioFormatted: futureDividendPayoutRatioFormatted,
    };
  }
);
